import { useCallback, useEffect, useMemo, useState } from 'react';

import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { CONTACT_ENTITY_LABEL } from '#lib/constants/entityLabels';
import { describeActionScope } from '#lib/describeActionScope';

import { useRowSelection } from '../../hooks/useRowSelection';

/**
 * Two mutually exclusive selection levels on the company table: companies (Gmail-style, can span
 * the whole filtered result) and contacts inside expanded company rows (visible rows only).
 * Selecting on one level clears the other, because every bulk action targets exactly one level.
 *
 * @param {Object} params
 * @param {Array<Object>} params.companies - The visible page
 * @param {number} params.totalCount
 * @param {Object} params.filters - Parsed URL filters, sent with selectAll
 * @param {string} params.filtersKey
 * @param {Map<string, Array<Object>>} params.contactsByCompany
 * @returns {{
 *   companySelection: Object, tableSelection: Object, contactSelection: {selectedIds: string[], clear: Function},
 *   isContactSelected: Function, handleToggleContact: Function, expandedCompanyIds: string[], handleToggleExpand: Function,
 *   actionScopeLabel: string,
 * }} `actionScopeLabel` names the selected contacts while there are any, since the action row then
 *   does not apply.
 */
export function useCompanySelection({ companies, totalCount, filters, filtersKey, contactsByCompany }) {
	const companySelection = useScopedSelection(companies, {
		totalCount,
		filters,
		resetKey: filtersKey,
		entityLabel: { singular: 'Unternehmen', plural: 'Unternehmen', gender: 'n' },
	});
	const { tableSelection, clear: clearCompanies, hasSelection: hasCompanySelection } = companySelection;

	const [expandedCompanyIds, setExpandedCompanyIds] = useState([]);

	const visibleContacts = useMemo(
		() => expandedCompanyIds.flatMap((id) => contactsByCompany.get(id) ?? []),
		[expandedCompanyIds, contactsByCompany],
	);

	// Unlike the company selection this survives changes of the visible set (expanding another
	// company changes it constantly); retainOnly prunes contacts that left the view instead.
	const {
		selected: selectedContacts,
		isSelected: isContactSelected,
		toggleOne: toggleContact,
		clearSelection: clearContacts,
		retainOnly: retainOnlyContacts,
	} = useRowSelection(visibleContacts);

	useEffect(() => {
		retainOnlyContacts(visibleContacts.map((c) => c.id));
	}, [visibleContacts, retainOnlyContacts]);

	const withoutContacts = useCallback(
		(handler) => (arg) => {
			if (selectedContacts.length > 0) clearContacts();
			handler(arg);
		},
		[selectedContacts.length, clearContacts],
	);

	const exclusiveTableSelection = useMemo(
		() => ({
			...tableSelection,
			onToggleOne: withoutContacts(tableSelection.onToggleOne),
			onToggleAll: withoutContacts(tableSelection.onToggleAll),
			scopeBanner: {
				...tableSelection.scopeBanner,
				onSelectAllInFilter: withoutContacts(tableSelection.scopeBanner.onSelectAllInFilter),
			},
		}),
		[tableSelection, withoutContacts],
	);

	const handleToggleContact = useCallback(
		(id) => {
			if (hasCompanySelection) clearCompanies();
			toggleContact(id);
		},
		[hasCompanySelection, clearCompanies, toggleContact],
	);

	const handleToggleExpand = useCallback((companyId) => {
		setExpandedCompanyIds((prev) =>
			prev.includes(companyId) ? prev.filter((x) => x !== companyId) : [...prev, companyId],
		);
	}, []);

	const actionScopeLabel =
		selectedContacts.length > 0
			? describeActionScope({ count: selectedContacts.length, isSelection: true, ...CONTACT_ENTITY_LABEL })
			: companySelection.actionScopeLabel;

	return {
		companySelection,
		actionScopeLabel,
		tableSelection: exclusiveTableSelection,
		contactSelection: { selectedIds: selectedContacts, clear: clearContacts },
		isContactSelected,
		handleToggleContact,
		expandedCompanyIds,
		handleToggleExpand,
	};
}
